import { getActiveSessionMetadata } from "lib/session-metadata";
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { appLogger } from "lib/datadog";
import { apiUploadFile, StepMetadata } from "lib/api";
import { Uploading } from "./components/Uploading";
import { Error as ErrorComponent } from "./components/Error";
import { Success } from "./components/Success";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Storage, StorageKey } from "lib/storage";
import { CaptureType } from "types";
import {
  getUploadFileValidationMessage,
  validateUploadFile,
} from "lib/upload-files";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { saveCompletedTaskSteps } from "lib/task-progress";
import { captureDB } from "lib/indexed-db";
import { StoredCollectionDetails } from "lib/get-collection-details";

enum Screens {
  App,
  Upload,
  Success,
  Error,
}

export enum UploadType {
  REGULAR = "regular",
  IRIS = "iris",
  PCR = "pcr",
}

export type UploadContextImages = Array<File>;

export interface UploadFileRequest {
  file: File;
  type: UploadType;
  taskId: string;
  captureGroupId?: string;
  existingCaptureId?: string;
  onCaptureId?: (captureId: string) => Promise<void>;
  stepMetadata?: StepMetadata;
  captureCollectionDetails?: StoredCollectionDetails | null;
  sessionMetadata?: Record<string, string>;
  onSuccess?: (captureId: string) => Promise<void>;
}

export interface UploadContextInterface {
  files: UploadContextImages;
  addFile: (file: File) => void;
  setFiles: (
    files: Array<File>,
    captureTypes?: Array<CaptureType | undefined>
  ) => void;
  resetFiles: () => void;
  removeFile: (idx: number) => void;
  captureId?: string;
  upload: (
    type?: UploadType,
    taskId?: string,
    stepMetadataMap?: Map<File, StepMetadata>,
    sessionMetadataMap?: Map<File, Record<string, string>>
  ) => Promise<boolean>;
  uploadBatch: (
    requests: UploadFileRequest[],
    completedCaptureIds?: string[]
  ) => Promise<boolean>;
  checkForDuplicates: () => Array<File>;
}

export const UploadContext = createContext({} as UploadContextInterface);

function isSameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.type === b.type;
}

let fallbackCaptureIdCounter = 0;

function createCaptureId(): string {
  if (typeof window?.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof window?.crypto?.getRandomValues === "function") {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  fallbackCaptureIdCounter = (fallbackCaptureIdCounter + 1) >>> 0;
  bytes[12] ^= fallbackCaptureIdCounter >>> 24;
  bytes[13] ^= fallbackCaptureIdCounter >>> 16;
  bytes[14] ^= fallbackCaptureIdCounter >>> 8;
  bytes[15] ^= fallbackCaptureIdCounter;
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

export const UploadContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const { contributor } = useContext(CollectionDetailsContext);
  const [screen, setScreen] = useState(Screens.App);
  const [files, setFiles] = useState<UploadContextImages>([]);
  const [captureId, setCaptureId] = useState<string>();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);

  const loc = useLocation();

  useEffect(() => {
    // Only reset screen if not currently uploading
    setScreen((currentScreen) => {
      if (currentScreen === Screens.Upload) {
        // Don't reset during upload to prevent white screen
        return currentScreen;
      }
      return Screens.App;
    });
  }, [loc.pathname]);

  const addFile = useCallback(
    async (file: File | unknown) =>
      setFiles((prev) => {
        if (!(file instanceof File)) {
          return prev;
        }

        const isPcrPage = loc.pathname.includes("pcr");
        const message = getUploadFileValidationMessage(
          file,
          isPcrPage ? "pcr" : undefined
        );
        if (message) {
          toast.warn(message);
          return prev;
        }

        return [...prev, file];
      }),
    [loc]
  );

  const _setFiles = useCallback(
    (files: Array<File>, captureTypes: Array<CaptureType | undefined> = []) => {
      const _files: Array<File> = [];
      const errors: string[] = [];
      const isPcrPage = loc.pathname.includes("pcr");

      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const error = validateUploadFile(
          file,
          captureTypes[index] ?? (isPcrPage ? "pcr" : undefined)
        );
        if (error === "pcr file type") {
          toast.warn("Only ZIP files are accepted for PCR uploads");
          continue;
        }
        if (error) {
          if (!errors.includes(error)) errors.push(error);
          continue;
        }

        _files.push(file);
      }

      if (errors.length > 0) {
        toast.warn(
          `Some files were not added due to errors: ${errors.join(", ")}.`
        );
      }

      setFiles(_files);
    },
    [loc]
  );

  const resetFiles = useCallback(() => setFiles([]), []);

  const removeFile = useCallback((idx: number) => {
    setFiles((prev) => prev.filter((_, _idx) => _idx !== idx));
  }, []);

  const uploadBatch = useCallback(
    async (
      requests: UploadFileRequest[],
      completedCaptureIds: string[] = []
    ): Promise<boolean> => {
      // Show upload screen immediately
      setUploadProgress(0);
      setScreen(Screens.Upload);

      if (requests.length === 0) {
        if (completedCaptureIds.length > 0) {
          setCaptureId(
            completedCaptureIds.length === 1
              ? completedCaptureIds[0]
              : undefined
          );
          Storage.remove(StorageKey.COLLECTION_DETAILS);
          setScreen(Screens.Success);
          return true;
        }
        setScreen(Screens.Error);
        setErrorMessage("At least one file is required for upload");
        return false;
      }

      try {
        const captureGroups = new Map<
          string,
          {
            captureId?: string;
            onCaptureId?: (captureId: string) => Promise<void>;
          }
        >();
        requests.forEach((request) => {
          const groupId = request.captureGroupId || "default";
          const group = captureGroups.get(groupId) || {};
          if (
            group.captureId &&
            request.existingCaptureId &&
            group.captureId !== request.existingCaptureId
          ) {
            throw new Error(
              `Capture Task ${request.taskId} has conflicting saved capture IDs.`
            );
          }
          captureGroups.set(groupId, {
            captureId: group.captureId || request.existingCaptureId,
            onCaptureId: group.onCaptureId || request.onCaptureId,
          });
        });

        const assignedCaptureIds = new Set<string>();
        for (const group of Array.from(captureGroups.values())) {
          let groupCaptureId = group.captureId;
          if (!groupCaptureId) {
            do {
              groupCaptureId = createCaptureId();
            } while (assignedCaptureIds.has(groupCaptureId));
          }
          if (assignedCaptureIds.has(groupCaptureId)) {
            throw new Error(
              "Different Capture Tasks cannot share one capture ID."
            );
          }
          assignedCaptureIds.add(groupCaptureId);
          group.captureId = groupCaptureId;
          await group.onCaptureId?.(groupCaptureId);
        }
        const allCaptureIds = new Set([
          ...completedCaptureIds,
          ...Array.from(assignedCaptureIds),
        ]);
        setCaptureId(
          allCaptureIds.size === 1 ? Array.from(allCaptureIds)[0] : undefined
        );

        const failedUploads: Array<{
          fileName: string;
          taskId: string;
          uploadType: UploadType;
          error: unknown;
        }> = [];
        const progressSaveFailures: Array<{
          fileName: string;
          error: unknown;
        }> = [];
        let progressSaveQueue = Promise.resolve();

        await Promise.all(
          requests.map(async (request) => {
            const requestCaptureId = captureGroups.get(
              request.captureGroupId || "default"
            )?.captureId;
            if (!requestCaptureId) {
              throw new Error(
                `Capture ID was not created for Capture Task ${request.taskId}.`
              );
            }
            try {
              await apiUploadFile(
                request.file,
                requestCaptureId,
                request.type,
                request.taskId,
                request.stepMetadata,
                request.captureCollectionDetails,
                request.sessionMetadata
              );
            } catch (error) {
              failedUploads.push({
                fileName: request.file.name,
                taskId: request.taskId,
                uploadType: request.type,
                error,
              });
              return;
            }

            if (request.onSuccess) {
              const currentSave = progressSaveQueue.then(() =>
                request.onSuccess!(requestCaptureId)
              );
              progressSaveQueue = currentSave.catch(() => undefined);
              try {
                await currentSave;
              } catch (error) {
                progressSaveFailures.push({
                  fileName: request.file.name,
                  error,
                });
              }
            }

            setUploadProgress((progress) => progress + 1);
          })
        );

        if (progressSaveFailures.length > 0) {
          appLogger.error("Uploaded file progress could not be saved.", {
            failedFiles: progressSaveFailures.map(({ fileName }) => fileName),
            errors: progressSaveFailures,
          });
          setScreen(Screens.Error);
          setErrorMessage(
            "Some files uploaded, but their local progress could not be saved. Do not retry; contact support."
          );
          return false;
        }

        if (failedUploads.length > 0) {
          const failedFileNames = failedUploads.map((f) => f.fileName);
          appLogger.error("Failed to upload image.", {
            images: requests.length,
            failedCount: failedUploads.length,
            failedFiles: failedFileNames,
            errors: failedUploads,
          });
          throw new Error(
            `Failed to upload ${failedUploads.length} of ${requests.length} files.`
          );
        }

        // Clear collection details after successful upload to ensure fresh data for next capture
        Storage.remove(StorageKey.COLLECTION_DETAILS);

        setScreen(Screens.Success);
        return true;
      } catch (err) {
        setScreen(Screens.Error);
        appLogger.error("Error submitting image.", err as object);
        setErrorMessage("Something went wrong, please try again.");
        return false;
      } finally {
        setFiles([]);
      }
    },
    []
  );

  const upload = useCallback(
    async (
      type = UploadType.REGULAR,
      taskId?: string,
      stepMetadataMap?: Map<File, StepMetadata>,
      sessionMetadataMap?: Map<File, Record<string, string>>
    ): Promise<boolean> => {
      if (!taskId) {
        setScreen(Screens.Error);
        setErrorMessage("Task ID is required for upload");
        return false;
      }

      const activeSessionMetadata = getActiveSessionMetadata();
      const succeeded = await uploadBatch(
        files.map((file) => ({
          file,
          type,
          taskId,
          stepMetadata: stepMetadataMap?.get(file),
          sessionMetadata:
            sessionMetadataMap?.get(file) ?? activeSessionMetadata,
        }))
      );

      if (!succeeded) return false;

      try {
        if (!stepMetadataMap) {
          await saveCompletedTaskSteps(taskId, contributor, {
            signature: "[]",
            stepNumbers: [1],
          });
        }
        await captureDB.deleteStepData(taskId, 0);
        return true;
      } catch (error) {
        appLogger.error(
          "Upload succeeded but collection details could not be cleared.",
          { taskId, error }
        );
        setScreen(Screens.Error);
        setErrorMessage(
          "Your files uploaded, but saved task details could not be cleared. Do not retry; contact support."
        );
        return false;
      }
    },
    [files, uploadBatch, contributor]
  );

  const checkForDuplicates = useCallback(() => {
    const duplicates: File[] = [];
    const seenFiles: File[] = [];

    for (const file of files) {
      if (seenFiles.some((seenFile) => isSameFile(file, seenFile))) {
        duplicates.push(file);
      } else {
        seenFiles.push(file);
      }
    }

    return duplicates;
  }, [files]);

  return (
    <UploadContext.Provider
      value={{
        files,
        addFile,
        setFiles: _setFiles,
        resetFiles,
        removeFile,
        upload,
        uploadBatch,
        captureId,
        checkForDuplicates,
      }}
    >
      {screen === Screens.App && children}
      {screen === Screens.Upload && (
        <Uploading files={files} progress={uploadProgress} />
      )}
      {screen === Screens.Success && <Success />}
      {screen === Screens.Error && <ErrorComponent message={errorMessage} />}
    </UploadContext.Provider>
  );
};
